import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  Settings,
  Database,
  Palette,
  Loader2,
  Save,
  RefreshCw,
  Globe,
  Clock,
  CheckCircle2
} from 'lucide-react';

import { API_BASE_URL as API } from '../utils/apiConfig';

export default function PlatformSettings() {
  const { isSuperAdmin } = useAuth();
  
  const [settings, setSettings] = useState({
    data_bridge_url: 'https://databridge.fundlezone.com/api/data-bridge',
    data_bridge_timeout: 30,
    default_theme_primary: '#8B5CF6',
    default_theme_secondary: '#EC4899'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState(null);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/settings`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setOriginalSettings(data);
        setHasChanges(false);
      } else {
        toast.error('Failed to load settings');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Error loading settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchSettings();
    }
  }, [fetchSettings, isSuperAdmin]);

  // Track changes
  useEffect(() => {
    if (originalSettings) {
      const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings);
      setHasChanges(changed);
    }
  }, [settings, originalSettings]);

  // Update a setting
  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Save settings
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Platform settings saved successfully');
        setOriginalSettings(data.settings);
        setHasChanges(false);
      } else {
        toast.error(data.detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  // Reset to original
  const handleReset = () => {
    if (originalSettings) {
      setSettings(originalSettings);
      setHasChanges(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <Settings className="w-12 h-12 mb-4 opacity-50" />
        <p>Only super admins can access Platform Settings</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="platform-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6 text-primary" />
            Platform Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure global settings for the Fundle Brain platform
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={fetchSettings} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Data Bridge Settings */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Database className="w-5 h-5 text-primary" />
          Data Bridge Configuration
        </div>
        <p className="text-sm text-muted-foreground">
          Configure the connection to the Fundle Data Bridge API. All brands will use this URL with their individual API keys.
        </p>
        
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="data-bridge-url" className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Data Bridge URL
            </Label>
            <Input
              id="data-bridge-url"
              placeholder="https://databridge.fundledata.com/api/data-bridge/"
              value={settings.data_bridge_url || ''}
              onChange={(e) => updateSetting('data_bridge_url', e.target.value)}
              data-testid="data-bridge-url-input"
            />
            <p className="text-xs text-muted-foreground">
              The base URL for the Data Bridge API. Each brand will append their API key for authentication.
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="data-bridge-timeout" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Request Timeout (seconds)
            </Label>
            <Input
              id="data-bridge-timeout"
              type="number"
              min={5}
              max={120}
              value={settings.data_bridge_timeout || 30}
              onChange={(e) => updateSetting('data_bridge_timeout', parseInt(e.target.value) || 30)}
              className="w-32"
              data-testid="data-bridge-timeout-input"
            />
          </div>
        </div>
      </div>

      {/* Default Theme Settings */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Palette className="w-5 h-5 text-primary" />
          Default Brand Theme
        </div>
        <p className="text-sm text-muted-foreground">
          Default colors for new brands. Individual brands can override these in their settings.
        </p>
        
        <div className="grid grid-cols-2 gap-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="default-primary">Primary Color</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                id="default-primary"
                value={settings.default_theme_primary || '#8B5CF6'}
                onChange={(e) => updateSetting('default_theme_primary', e.target.value)}
                className="w-10 h-10 rounded cursor-pointer"
              />
              <Input
                value={settings.default_theme_primary || '#8B5CF6'}
                onChange={(e) => updateSetting('default_theme_primary', e.target.value)}
                className="flex-1"
                data-testid="default-primary-color"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="default-secondary">Secondary Color</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                id="default-secondary"
                value={settings.default_theme_secondary || '#EC4899'}
                onChange={(e) => updateSetting('default_theme_secondary', e.target.value)}
                className="w-10 h-10 rounded cursor-pointer"
              />
              <Input
                value={settings.default_theme_secondary || '#EC4899'}
                onChange={(e) => updateSetting('default_theme_secondary', e.target.value)}
                className="flex-1"
                data-testid="default-secondary-color"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save Bar */}
      <div className={`sticky bottom-4 transition-all duration-200 ${hasChanges ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <div className="rounded-xl border border-primary/50 bg-card p-4 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-2 text-sm">
            <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
            You have unsaved changes
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleReset}>
              Reset
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      {/* Last Updated Info */}
      {settings.updated_at && (
        <div className="text-xs text-muted-foreground flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" />
          Last updated: {new Date(settings.updated_at).toLocaleString()} by {settings.updated_by}
        </div>
      )}
    </div>
  );
}
